import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { MobileFrame } from "@/components/MobileFrame";
import { pickDefaultAvatar } from "@/lib/defaultAvatar";

/**
 * Landing page for all post-auth redirects (Google OAuth, email confirmation).
 *
 * Supabase processes the URL token/code automatically; by the time our
 * useEffect fires we either already have a session (via getSession) or we
 * listen for the SIGNED_IN event. Once we have a session we:
 *   1. Ensure the profiles row exists (trigger fallback).
 *   2. Check onboarding_complete.
 *   3. Route to /onboard (first-timers) or /home (returning users).
 */
export default function AuthCallback() {
  const nav = useNavigate();

  useEffect(() => {
    // TODO(post-ship): if user-reported duplicate-account confusion appears,
    // add a Settings → Connected Accounts panel that surfaces auth.identities
    // and offers manual linking via supabase.auth.linkIdentity({provider:'google'}).
    let handled = false;

    const route = async (userId: string) => {
      if (handled) return;
      handled = true;

      // Ensure the profiles row exists. The DB trigger creates it on
      // auth.users INSERT, but race conditions or trigger failures are
      // possible — this upsert is a safe fallback.
      const { data: existing } = await supabase
        .from("profiles")
        .select("onboarding_complete")
        .eq("id", userId)
        .maybeSingle();

      // For first-time users (no profile yet) coming from the OAuth signup path,
      // gate by waitlist approval. If their email isn't on the approved list,
      // sign them out and bounce back to /signup with an error.
      if (!existing) {
        const { data: userData } = await supabase.auth.getUser();
        const userEmail = userData?.user?.email ?? "";
        if (userEmail) {
          const { data: approved } = await supabase.rpc("check_waitlist_approved", {
            check_email: userEmail.toLowerCase(),
          });
          if (!approved) {
            await supabase.auth.signOut().catch(() => {});
            nav("/signup", { replace: true, state: { error: "This account isn't approved on the waitlist yet." } });
            return;
          }
        }
      }

      if (!existing) {
        // Profile missing — create it. The trigger should have run, but
        // if it didn't we need a valid handle. We'll use the email local
        // part, cleaned to allowed chars. Uniqueness collisions are
        // handled by the DB (unique constraint will just reject the upsert
        // and the user will still proceed to /onboard where they can fix
        // their handle).
        const { data: userData } = await supabase.auth.getUser();
        const u = userData?.user;
        const metaHandle = (u?.user_metadata?.handle as string | undefined)?.replace(/[^a-z0-9_]/gi, "").toLowerCase();
        const emailLocal = (u?.email ?? "").split("@")[0].replace(/[^a-z0-9_]/gi, "").toLowerCase();
        const handle = metaHandle || emailLocal || "picker";
        // Prefer the handle the user chose at sign-up; fall back to OAuth name
        // fields only if no explicit handle is present (Google sign-in).
        const displayName =
          metaHandle ??
          u?.user_metadata?.full_name ??
          u?.user_metadata?.name ??
          handle;
        await supabase.from("profiles").upsert(
          {
            id: userId,
            handle,
            display_name: displayName,
            // Pick a deterministic-by-uid swatch so the default-avatar
            // pool reads as designed variety rather than "everyone got
            // the same stripes." Users can change it later.
            avatar: pickDefaultAvatar(userId),
            onboarding_complete: false,
            // walkthrough_complete starts FALSE so the first-time
            // /home spotlight tour can run. The Tutorial.finish()
            // path flips onboarding_complete → true, and WalkthroughOverlay
            // gates on `onboarding_complete && !walkthrough_complete`.
            walkthrough_complete: false,
            subscription_tier: "none",
            free_scans_remaining: 5,
          } as never,
          { onConflict: "id", ignoreDuplicates: true },
        );
      }

      const done = existing?.onboarding_complete ?? false;

      // Admins skip onboarding.
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      if (done || adminRole) {
        nav("/home", { replace: true });
      } else {
        nav("/onboard", { replace: true });
      }
    };

    // Check for an already-resolved session first (e.g., magic link, PKCE
    // code already exchanged by the time the component mounts).
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) route(session.user.id);
    });

    // Also subscribe so we catch the SIGNED_IN event if the code exchange
    // is still in flight when the component mounts.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) route(session.user.id);
    });

    // If nothing resolves in 12 s, send the user back to /auth.
    const timeout = setTimeout(() => {
      if (!handled) nav("/auth", { replace: true });
    }, 12_000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [nav]);

  return (
    <MobileFrame>
      <div
        style={{
          minHeight: "100vh",
          background: "hsl(var(--paper))",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 900,
            fontStyle: "italic",
            fontSize: 48,
            color: "hsl(var(--ink))",
            letterSpacing: "-0.04em",
          }}
        >
          fig
        </div>
        <span
          className="caps"
          style={{ color: "hsl(var(--ink-2))", letterSpacing: "0.15em" }}
        >
          signing you in…
        </span>
      </div>
    </MobileFrame>
  );
}
